/**
 * @module services/urgency-queue-order
 * @description Orden y renumeración de la cola de urgencias, extraídos de
 *              `UrgencyQueue.vue` para poder testearlos sin montar el SFC.
 *              UC-REG-276: la vista ordenaba con `priority || 999`, con lo que
 *              un caso con prioridad 0 (el default) se veía ÚLTIMO mientras
 *              la API (y la posición que ve el paciente) lo ponía PRIMERO; y
 *              subir/bajar intercambiaba prioridades, que entre dos 0 no hacía
 *              nada. Ahora la vista usa el mismo orden que la API y cualquier
 *              movimiento renumera 1..N a los que esperan.
 */

/** @description Estados que cuentan como "en espera" (incluye legacy `pending`) */
export const WAITING_STATUSES = ["waiting", "pending"];

/**
 * @description Si el caso está esperando (se puede tomar, mover o cancelar).
 * @param {Object} item - Caso de la cola
 * @returns {boolean}
 */
export function isWaiting(item) {
  return !!item && WAITING_STATUSES.includes(item.status);
}

/**
 * @description Milisegundos de una fecha ISO (0 si falta).
 * @param {string|Date} date
 * @returns {number}
 */
function time(date) {
  return date ? new Date(date).getTime() : 0;
}

/**
 * @description Mismo orden que `GET /urgency-queue/specialty/:id`: prioridad
 *              ascendente (0 = sin prioridad asignada, va primero) y, a igual
 *              prioridad, orden de llegada. No muta la lista.
 * @param {Object[]} items
 * @returns {Object[]}
 */
export function sortQueue(items) {
  return [...(items || [])].sort((a, b) => {
    const pa = Number(a.priority) || 0;
    const pb = Number(b.priority) || 0;
    if (pa !== pb) return pa - pb;
    return time(a.requestedAt) - time(b.requestedAt);
  });
}

/**
 * @description Copia de la lista con el elemento `from` movido a la posición
 *              `to`. Índices fuera de rango o iguales → copia sin cambios.
 * @param {Object[]} list
 * @param {number} from
 * @param {number} to
 * @returns {Object[]}
 */
export function moveItem(list, from, to) {
  const next = [...list];
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= next.length ||
    to >= next.length
  ) {
    return next;
  }
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/**
 * @description Prioridades 1..N para los casos en espera según el orden de la
 *              lista (los que no esperan se saltean: la API rechaza cambiarles
 *              la prioridad). Devuelve solo los que cambian.
 * @param {Object[]} list - Cola en el orden deseado
 * @returns {{ id: string, priority: number }[]}
 */
export function renumberUpdates(list) {
  const updates = [];
  let n = 0;
  (list || []).forEach((item) => {
    if (!isWaiting(item)) return;
    n += 1;
    if (item.priority !== n) updates.push({ id: item._id, priority: n });
  });
  return updates;
}
